import type { OperationsDependency, OperationsSnapshot } from '@blume/web-test-model'
import { Ban, CircleCheck, CircleHelp, TriangleAlert, type LucideIcon } from 'lucide-react'

import { formatBytes, formatTime, relativeTime } from '@/design/format'
import { toneText, type Tone } from '@/design/status'
import { cn } from '@/lib/utils'

import { GroupCard, KeyValueList, PageTitle, SectionLabel } from './parts'

const dependencyMeta: Record<
  OperationsDependency['status'],
  { label: string; icon: LucideIcon; tone: Tone }
> = {
  healthy: { label: 'Healthy', icon: CircleCheck, tone: 'pass' },
  degraded: { label: 'Degraded', icon: TriangleAlert, tone: 'warn' },
  disabled: { label: 'Disabled', icon: Ban, tone: 'neutral' },
  unknown: { label: 'Unknown', icon: CircleHelp, tone: 'neutral' }
}

const overallHeadline: Record<OperationsSnapshot['status'], string> = {
  healthy: 'All systems healthy',
  degraded: 'Service degraded',
  disabled: 'Service disabled'
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[28px] font-semibold tracking-tight tabular-nums">{value}</p>
      <p className="text-[13px] text-muted-foreground">{label}</p>
    </div>
  )
}

function BooleanValue({ value, on, off }: { value: boolean; on: string; off: string }) {
  return <span>{value ? on : off}</span>
}

/**
 * A snapshot older than this cannot honestly headline present-tense health.
 * The model stays untouched — freshness is derived from `generatedAt` alone.
 */
const STALE_SNAPSHOT_MS = 60 * 60 * 1_000

export function OperationsPage({ snapshot }: { snapshot: OperationsSnapshot }) {
  const overall =
    dependencyMeta[
      snapshot.status === 'healthy'
        ? 'healthy'
        : snapshot.status === 'degraded'
          ? 'degraded'
          : 'disabled'
    ]
  const OverallIcon = overall.icon
  const now = Date.now()
  const generatedTime = new Date(snapshot.generatedAt).getTime()
  const stale = !Number.isFinite(generatedTime) || now - generatedTime > STALE_SNAPSHOT_MS
  const age = relativeTime(snapshot.generatedAt, now)
  return (
    <>
      <PageTitle
        title="Operations"
        subtitle="Service health, stored evidence, and capacity controls for the verifier. Use this page to understand whether the testing platform itself is ready to run work."
      />
      <p className="-mt-5 mb-8 text-sm text-muted-foreground">
        Snapshot generated {formatTime(snapshot.generatedAt)}
      </p>

      <p className="flex items-center gap-2.5 text-xl font-semibold tracking-tight">
        <OverallIcon
          aria-hidden
          className={cn(
            'size-5',
            snapshot.status === 'healthy' ? 'text-muted-foreground' : toneText[overall.tone]
          )}
        />
        <span>
          {overallHeadline[snapshot.status]}
          {stale ? <span className="font-normal text-muted-foreground"> — as of {age}</span> : null}
        </span>
      </p>

      {stale ? (
        <p className="mt-3 flex max-w-prose items-start gap-2 text-sm leading-6">
          <TriangleAlert aria-hidden className="mt-1 size-3.5 shrink-0 text-status-warn" />
          <span>
            <span className={cn('font-medium', toneText.warn)}>Stale snapshot.</span>{' '}
            <span className="text-muted-foreground">
              Everything on this page describes the service as of {age}, not right now — treat
              current health as unknown until a fresh snapshot is generated.
            </span>
          </span>
        </p>
      ) : null}

      <div className="mt-10 grid grid-cols-2 gap-x-8 gap-y-6 sm:grid-cols-4">
        <Stat label="Total runs" value={String(snapshot.activity.totalRuns)} />
        <Stat label="Runs, last 24h" value={String(snapshot.activity.runs24h)} />
        <Stat label="Active now" value={String(snapshot.activity.activeRuns)} />
        <Stat label="Evidence stored" value={formatBytes(snapshot.activity.artifactBytes)} />
      </div>

      <section aria-label="Dependencies" className="mt-14">
        <GroupCard header={<SectionLabel>Dependencies</SectionLabel>}>
          <ul className="space-y-4 pb-1">
            {snapshot.dependencies.map((dependency) => {
              const meta = dependencyMeta[dependency.status]
              const Icon = meta.icon
              const quiet = meta.tone === 'pass' || meta.tone === 'neutral'
              return (
                <li key={dependency.id} className="flex items-baseline gap-3">
                  <Icon
                    aria-hidden
                    className={cn(
                      'size-4 shrink-0 self-center',
                      quiet ? 'text-muted-foreground' : toneText[meta.tone]
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-baseline gap-x-3">
                      <span className="text-sm font-medium">{dependency.label}</span>
                      <span
                        className={cn(
                          'text-[13px] font-medium',
                          quiet ? 'text-muted-foreground' : toneText[meta.tone]
                        )}
                      >
                        {meta.label}
                      </span>
                    </p>
                    <p className="mt-0.5 text-[13px] leading-6 text-muted-foreground">
                      {dependency.detail}
                    </p>
                  </div>
                </li>
              )
            })}
          </ul>
        </GroupCard>
      </section>

      <div className="mt-14 grid grid-cols-1 gap-x-16 gap-y-12 lg:grid-cols-2">
        <section aria-label="Capacity and limits">
          <SectionLabel>Capacity &amp; limits</SectionLabel>
          <div className="mt-3">
            <KeyValueList
              items={[
                { key: 'Active runs', value: snapshot.limits.activeRuns },
                {
                  key: 'Active runs per repository',
                  value: snapshot.limits.activeRunsPerRepository
                },
                {
                  key: 'Active runs per pull request',
                  value: snapshot.limits.activeRunsPerPullRequest
                },
                { key: 'Runs per actor per hour', value: snapshot.limits.runsPerActorHour },
                {
                  key: 'Model requests per actor per hour',
                  value: snapshot.limits.modelRequestsPerActorHour
                },
                {
                  key: 'Requests per actor per minute',
                  value: snapshot.limits.requestsPerActorMinute
                },
                { key: 'Artifact budget', value: formatBytes(snapshot.limits.artifactBytes) },
                {
                  key: 'Artifact budget per run',
                  value: formatBytes(snapshot.limits.artifactBytesPerRun)
                }
              ]}
            />
          </div>
        </section>

        <div className="space-y-12">
          <section aria-label="Evidence retention">
            <SectionLabel>Retention</SectionLabel>
            <div className="mt-3">
              <KeyValueList
                items={[
                  {
                    key: 'Passing evidence',
                    value: `${snapshot.retention.passingEvidenceDays} days`
                  },
                  { key: 'Passing reports', value: `${snapshot.retention.passingReportDays} days` },
                  {
                    key: 'Non-passing evidence',
                    value: `${snapshot.retention.nonPassingEvidenceDays} days`
                  },
                  {
                    key: 'Scheduled cleanup',
                    value: (
                      <BooleanValue
                        value={snapshot.retention.scheduledCleanup}
                        on="Enabled"
                        off="Disabled"
                      />
                    )
                  }
                ]}
              />
            </div>
          </section>

          <section aria-label="Service controls">
            <SectionLabel>Controls</SectionLabel>
            <div className="mt-3">
              <KeyValueList
                items={[
                  {
                    key: 'Accepting runs',
                    value: (
                      <BooleanValue value={snapshot.controls.acceptingRuns} on="Yes" off="No" />
                    )
                  },
                  {
                    key: 'AI inference',
                    value: (
                      <BooleanValue
                        value={snapshot.controls.aiInferenceEnabled}
                        on="Enabled"
                        off="Disabled"
                      />
                    )
                  },
                  {
                    key: 'CI repository',
                    value: <span className="font-mono">{snapshot.controls.ciRepository}</span>
                  },
                  {
                    key: 'Preview host suffixes',
                    value: (
                      <span className="font-mono">
                        {snapshot.controls.previewHostSuffixes.join(', ')}
                      </span>
                    )
                  },
                  {
                    key: 'Public DNS required',
                    value: (
                      <BooleanValue
                        value={snapshot.controls.dnsPublicAddressRequired}
                        on="Yes"
                        off="No"
                      />
                    )
                  },
                  {
                    key: 'Origin isolation',
                    value: (
                      <BooleanValue
                        value={snapshot.controls.originIsolationEnabled}
                        on="Enabled"
                        off="Disabled"
                      />
                    )
                  }
                ]}
              />
            </div>
          </section>
        </div>
      </div>
    </>
  )
}
